import React from "react";
import ReactDOM from "react-dom/client";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import DefaultReport from "../DefaultReport/DefaultReport";

const loadImages = (container) => {
  return new Promise((resolve) => {
    const imgs = container.getElementsByTagName("img");
    if (imgs.length === 0) return resolve();
    let loaded = 0;
    for (let img of imgs) {
      if (img.complete) {
        loaded++;
        if (loaded === imgs.length) resolve();
      } else {
        img.onload = img.onerror = () => {
          loaded++;
          if (loaded === imgs.length) resolve();
        };
      }
    }
  });
};

const defaultVoucherPrint = async (
  clinic,
  qrCodeUrl,
  selectedItem,
  referrer,
  extras = {},
) => {
  const container = document.createElement("div");
  container.style.position = "fixed";
  container.style.top = "-9999px";
  container.style.left = "-9999px";
  container.style.width = "210mm";
  container.style.background = "#fff";
  document.body.appendChild(container);
  const fullInvoiceData = selectedItem;
  const appointmentDetails = selectedItem?.appointmentDetails || {};
  // Build the item data with appointment details included
  const itemData = {
    ...selectedItem,
    ...(selectedItem?.invoiceId?.[0]?.appointmentDetails || {}),
    patientName: selectedItem?.patientId?.userId?.name,
    age: selectedItem?.patientId?.userId?.age,
    gender: selectedItem?.patientId?.userId?.gender,
    patientId: selectedItem?.patientId,
    createdDt: selectedItem?.createdDt,
    updatedDt: selectedItem?.updatedDt,
  };

  const root = ReactDOM.createRoot(container);
  root.render(
    <DefaultReport
      clinic={clinic}
      qrCode={qrCodeUrl}
      address={extras.address}
      selectedItem={appointmentDetails} // Patient details
      item={fullInvoiceData}
      referrer={referrer}
      watermark={extras.watermark}
      isoLogo={extras.isoLogo}
      footer={extras.footer}
      forPrint={true}
    />,
  );

  const inlineImages = async (rootEl) => {
    const imgs = Array.from(rootEl.getElementsByTagName("img"));
    for (const img of imgs) {
      const src = img.src;
      if (!src) continue;

      if (src.startsWith("data:") || src.startsWith("blob:")) continue;

      try {
        const res = await fetch(src, { mode: "cors" });
        if (!res.ok) throw new Error();

        const blob = await res.blob();
        const reader = new FileReader();

        const dataUrl = await new Promise((resolve, reject) => {
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        img.src = dataUrl;
      } catch (e) {}
    }
  };

  const setCrossOrigin = (rootEl) => {
    const imgs = Array.from(rootEl.getElementsByTagName("img"));
    for (const img of imgs) {
      try {
        img.crossOrigin = img.crossOrigin || "anonymous";
      } catch (e) {}
    }
  };

  setCrossOrigin(container);
  await inlineImages(container);

  await loadImages(container);
  await new Promise((r) => setTimeout(r, 500));
  const finalImgs = container.getElementsByTagName("img");
  const failed = Array.from(finalImgs).filter((i) => i.naturalWidth === 0);
  if (failed.length) {
    console.warn(
      "defaultVoucherPrint: some images failed to load for PDF:",
      failed.map((i) => i.src),
    );
  }

  const pageDiv = container.querySelector('div[class*="page"]');
  if (pageDiv) {
    pageDiv.style.overflow = "visible";
  }

  container.style.overflow = "visible";
  container.style.width = "210mm";

  await new Promise((r) => setTimeout(r, 300));

  const scrollHeight = pageDiv ? pageDiv.scrollHeight : container.scrollHeight;

  try {
    if (document.fonts?.ready) {
      await document.fonts.ready;
    }
  } catch (e) {}

  const canvas = await html2canvas(container, {
    scale: 2,
    useCORS: true,
    allowTaint: false,
    imageTimeout: 15000,
    windowHeight: scrollHeight,
  });

  const imgData = canvas.toDataURL("image/png");
  const pdf = new jsPDF("p", "mm", "a4");

  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

  pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
  pdf.save("DefaultReport.pdf");
  root.unmount();
  document.body.removeChild(container);
};

export default defaultVoucherPrint;
